import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { PaginatorModule } from 'primeng/paginator';
import { Loading } from '@app/components/ui/loading/loading';
import { UploadedGeoImagesStateService } from './services/uploaded-geo-images-state.service';
import { UploadedGeoImageService } from './services/uploaded-geo-image.service';
import { UploadedGeoImageListItem, UploadedGeoImageUpdateRequest } from './services/uploaded-geo-image.models';
import { GeoImageDetailModalComponent } from './geo-image-detail-modal/geo-image-detail-modal.component';
import { GeoImageEditModalComponent } from './geo-image-edit-modal/geo-image-edit-modal.component';
import { GeoImageDeleteModalComponent } from './geo-image-delete-modal/geo-image-delete-modal.component';

/** One rendered card, precomputed once per `pagedItems`/`selectedId` change instead of per change-detection pass. */
interface GeoImageCardViewModel {
  item: UploadedGeoImageListItem;
  thumbnailUrl: string;
  regionPath: string;
  tagsText: string;
  isSelected: boolean;
}

/**
 * List of the "Uploaded Geo-Images" tab (FR-010): a card per image with thumbnail, capture date/time and
 * tags, a running total, and the preview / edit / delete actions (FR-013, FR-014). It renders in the page's
 * list column; the map and the filter panel render in the other two columns and share the state service.
 */
@Component({
  selector: 'app-uploaded-geo-images',
  standalone: true,
  templateUrl: './uploaded-geo-images.component.html',
  styleUrls: ['./uploaded-geo-images.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, PaginatorModule, Loading, GeoImageDetailModalComponent, GeoImageEditModalComponent, GeoImageDeleteModalComponent]
})
export class UploadedGeoImagesComponent {
  /** Shown when there is no image to draw, or the image fails to load. */
  private static readonly FALLBACK_IMAGE = 'assets/icons/main/light/upload-img-bg.svg';

  readonly cards = computed<GeoImageCardViewModel[]>(() => {
    const selectedId = this.state.selectedId();
    return this.state.pagedItems().map(item => ({
      item,
      thumbnailUrl: this.thumbnailUrl(item),
      regionPath: this.regionPath(item),
      tagsText: this.tagsText(item),
      isSelected: item.id === selectedId
    }));
  });

  detailItem: UploadedGeoImageListItem | null = null;
  detailVisible = false;

  editItem: UploadedGeoImageListItem | null = null;
  editVisible = false;
  saving = false;

  deleteItem: UploadedGeoImageListItem | null = null;
  deleteVisible = false;
  deleting = false;

  constructor(
    public readonly state: UploadedGeoImagesStateService,
    private readonly api: UploadedGeoImageService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  trackById(_index: number, card: GeoImageCardViewModel): string {
    return card.item.id;
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement | null;
    if (target) {
      target.src = UploadedGeoImagesComponent.FALLBACK_IMAGE;
    }
  }

  onCardClick(item: UploadedGeoImageListItem): void {
    this.state.select(this.state.selectedId() === item.id ? null : item.id);
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const rows = event.rows || this.state.pageSize;
    this.state.setPage(Math.floor((event.first ?? 0) / rows) + 1);
  }

  openPreview(item: UploadedGeoImageListItem, event?: Event): void {
    event?.stopPropagation();
    this.detailItem = item;
    this.detailVisible = true;
  }

  openEdit(item: UploadedGeoImageListItem, event?: Event): void {
    event?.stopPropagation();
    this.detailVisible = false;
    this.editItem = item;
    this.editVisible = true;
  }

  openDelete(item: UploadedGeoImageListItem, event?: Event): void {
    event?.stopPropagation();
    this.detailVisible = false;
    this.deleteItem = item;
    this.deleteVisible = true;
  }

  saveEdit(request: UploadedGeoImageUpdateRequest): void {
    if (this.saving) {
      return;
    }
    this.saving = true;

    this.api.update(request).subscribe({
      next: updated => {
        this.saving = false;
        this.editVisible = false;
        this.state.replaceItem(updated);
        this.notify('success', 'UPLOADED_GEO_IMAGES.TOAST_UPDATED');
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.notify('error', 'UPLOADED_GEO_IMAGES.TOAST_UPDATE_FAILED');
        this.cdr.markForCheck();
      }
    });
  }

  confirmDelete(): void {
    if (!this.deleteItem || this.deleting) {
      return;
    }
    const id = this.deleteItem.id;
    this.deleting = true;

    this.api.delete(id).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteVisible = false;
        this.state.removeItem(id);
        this.notify('success', 'UPLOADED_GEO_IMAGES.TOAST_DELETED');
        this.cdr.markForCheck();
      },
      error: () => {
        this.deleting = false;
        this.notify('error', 'UPLOADED_GEO_IMAGES.TOAST_DELETE_FAILED');
        this.cdr.markForCheck();
      }
    });
  }

  /** The card image, or the placeholder for a raster whose preview could not be generated. */
  private thumbnailUrl(item: UploadedGeoImageListItem): string {
    return this.api.getThumbnailUrl(item, 240, 180) ?? UploadedGeoImagesComponent.FALLBACK_IMAGE;
  }

  private regionPath(item: UploadedGeoImageListItem): string {
    return [item.parentRegionName, item.regionName].filter((part): part is string => Boolean(part)).join(' / ');
  }

  private tagsText(item: UploadedGeoImageListItem): string {
    return item.tags.map(tag => tag.name).join(', ');
  }

  private notify(severity: 'success' | 'error', messageKey: string): void {
    this.messageService.add({ severity, summary: this.translate.instant(messageKey), life: 4000 });
  }
}
